import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { Badge, Button, Progress } from '../ui';

/**
 * One path in the overview (spec §10.4 "Pfadübersicht").
 *
 * An assigned path says so, and says what that implies: assigned onboarding
 * content is the only place in Etch where a learning object appears without
 * the user confirming it (spec §9.2). Stating that here is what keeps the
 * "normale Chats erzeugen nichts ungefragt" promise legible rather than
 * merely true.
 */
export function PathCard({ path, onOpen }) {
  const { t } = useTranslation();
  const headingId = useId();

  const title = path.titleKey ? t(path.titleKey) : path.title;
  const description = path.descriptionKey ? t(path.descriptionKey) : path.description;
  const { progress } = path;

  const chapterCount = path.chapters.length || path.summaryCounts?.modules || 0;
  const openLabel = progress.done
    ? t('onboarding.openPath')
    : progress.started
      ? t('onboarding.resume')
      : t('onboarding.startPath');

  return (
    <article className="etch-path-card" aria-labelledby={headingId} data-required={path.required || undefined}>
      <div className="etch-path-card__head">
        <h3 className="etch-path-card__title" id={headingId}>
          {title}
        </h3>
        <Badge tone={path.required ? 'brand' : undefined}>
          {path.required ? t('onboarding.required') : t('onboarding.optional')}
        </Badge>
        {path.assigned && (
          <Badge tone="signal">
            <ShieldCheck size={12} aria-hidden="true" /> {t('onboarding.assigned')}
          </Badge>
        )}
      </div>

      {description && <p className="etch-path-card__description">{description}</p>}

      <p className="etch-path-card__note">
        {path.assigned ? t('onboarding.assignedNote') : t('onboarding.selfMadeNote')}
      </p>

      <dl className="etch-path-card__facts">
        <div>
          <dt>{t('onboarding.chapter')}</dt>
          <dd>{chapterCount}</dd>
        </div>
        <div>
          <dt>{t('onboarding.progress')}</dt>
          <dd>{t('onboarding.progressOf', { completed: progress.completed, total: progress.total })}</dd>
        </div>
        {progress.requiredTotal > 0 && (
          <div>
            <dt>{t('onboarding.required')}</dt>
            <dd>
              {t('onboarding.requiredProgress', {
                completed: progress.requiredCompleted,
                total: progress.requiredTotal,
              })}
            </dd>
          </div>
        )}
      </dl>

      <Progress
        value={progress.completed}
        max={Math.max(progress.total, 1)}
        showValue={false}
        ariaLabel={t('onboarding.progressOf', { completed: progress.completed, total: progress.total })}
      />

      {path.kinds.length > 0 && (
        <p className="etch-path-card__kinds">
          <span className="etch-visually-hidden">{t('onboarding.kindLegend')}: </span>
          {path.kinds.map((kind) => (
            <Badge key={kind}>{t(`onboarding.kind.${kind}`)}</Badge>
          ))}
        </p>
      )}

      <div className="etch-path-card__actions">
        <Button variant="secondary" size="compact" onClick={() => onOpen(path)}>
          {openLabel}
          <span className="etch-visually-hidden"> — {title}</span>
        </Button>
      </div>
    </article>
  );
}

export default PathCard;
